import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, ListTodo, Search } from "lucide-react";

const TIPI = [["todo", "To-do"], ["promemoria", "Promemoria"], ["appuntamento", "Appuntamento"]];
const nomeCliente = (c) => [c.nome, c.cognome].filter(Boolean).join(" ").trim() || "Senza nome";

export default function AssegnaCompitoDialog({ onClose, onSaved, defaultUserId, pratica }) {
  const qc = useQueryClient();
  const { data: utenti } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const { data: clienti } = useQuery({ queryKey: ["clienti"], queryFn: async () => (await api.get("/clienti")).data });
  const [form, setForm] = useState({
    titolo: "", tipo: "todo", note: "", assegnato_a: defaultUserId || "", scadenza: "", durata_min: 60,
  });
  const [cliente, setCliente] = useState(pratica?.cliente
    ? { id: pratica.cliente_id, nome: pratica.cliente.nome, cognome: pratica.cliente.cognome }
    : null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const trovati = (clienti || []).filter((c) => q.trim() && nomeCliente(c).toLowerCase().includes(q.trim().toLowerCase())).slice(0, 8);

  const salva = async () => {
    if (!form.titolo.trim()) return toast.error("Indica cosa c'è da fare");
    if (!form.assegnato_a) return toast.error("Scegli a chi assegnare il compito");
    if (form.tipo === "appuntamento" && !form.scadenza) return toast.error("Per un appuntamento serve data e ora");
    setSaving(true);
    try {
      await api.post("/compiti", { ...form, cliente_id: cliente?.id || null,
        pratica_id: pratica?.id || null, durata_min: Number(form.durata_min) || 60 });
      toast.success(form.tipo === "appuntamento" ? "Appuntamento assegnato e messo in calendario" : "Compito assegnato");
      qc.invalidateQueries({ queryKey: ["compiti"] });
      qc.invalidateQueries({ queryKey: ["mio-giorno"] });
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'assegnazione");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#16233c]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="assegna-compito-dialog">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4 border-t-2 border-[#C9A84C] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl text-[#1A2B4A] flex items-center gap-2">
            <ListTodo className="w-5 h-5 text-[#C9A84C]" /> Assegna un compito
          </h3>
          <button data-testid="compito-close" onClick={onClose} className="text-[#4a5568] hover:text-[#1A2B4A]"><X className="w-5 h-5" /></button>
        </div>

        {pratica && (
          <p data-testid="compito-contesto-pratica" className="text-[11px] text-[#8a6d1f] bg-[#fbf7ec] border border-[#C9A84C]/40 rounded-sm px-2.5 py-1.5 mb-4">
            Collegato alla pratica <b>{pratica.tipo_pratica_nome}</b>
            {pratica.cliente ? ` di ${[pratica.cliente.nome, pratica.cliente.cognome].filter(Boolean).join(" ")}` : ""}
          </p>
        )}

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Tipo</label>
        <div className="flex gap-2 mt-1 mb-4">
          {TIPI.map(([v, label]) => (
            <button key={v} type="button" data-testid={`compito-tipo-${v}`} onClick={() => set("tipo", v)}
              className={`px-3 py-1.5 text-sm rounded-sm border transition-colors ${form.tipo === v
                ? "bg-[#1A2B4A] text-white border-[#1A2B4A]"
                : "bg-white text-[#1A2B4A] border-[#1A2B4A]/20 hover:border-[#C9A84C]"}`}>{label}</button>
          ))}
        </div>

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Cosa c'è da fare</label>
        <input data-testid="compito-titolo" value={form.titolo} onChange={(e) => set("titolo", e.target.value)}
          placeholder="es. Chiamare il cliente per il permesso"
          className="w-full mt-1 mb-4 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">A chi</label>
            <select data-testid="compito-assegnatario" value={form.assegnato_a} onChange={(e) => set("assegnato_a", e.target.value)}
              className="w-full mt-1 px-2.5 py-2 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]">
              <option value="">— scegli —</option>
              {(utenti || []).map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">
              {form.tipo === "appuntamento" ? "Data e ora" : "Entro il"}
            </label>
            <input data-testid="compito-scadenza" type={form.tipo === "appuntamento" ? "datetime-local" : "date"}
              value={form.scadenza} onChange={(e) => set("scadenza", e.target.value)}
              className="w-full mt-1 px-2.5 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
        </div>

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Cliente collegato (facoltativo)</label>
        <div className="relative mt-1 mb-4">
          <div className="flex items-center border border-[#1A2B4A]/20 rounded-sm px-2.5 focus-within:border-[#C9A84C]">
            <Search className="w-4 h-4 text-[#4a5568] shrink-0" />
            <input data-testid="compito-cliente-search" value={cliente ? nomeCliente(cliente) : q}
              onChange={(e) => { setQ(e.target.value); setCliente(null); }} placeholder="Cerca un cliente…"
              className="w-full px-2 py-2 bg-transparent text-sm focus:outline-none" />
            {(cliente || q) && <button onClick={() => { setCliente(null); setQ(""); }} className="text-[#4a5568]"><X className="w-4 h-4" /></button>}
          </div>
          {trovati.length > 0 && !cliente && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-[#1A2B4A]/20 rounded-sm shadow-lg max-h-40 overflow-y-auto">
              {trovati.map((c) => (
                <button key={c.id} data-testid={`compito-cliente-option-${c.id}`} onClick={() => { setCliente(c); setQ(""); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#f9f8f5]">{nomeCliente(c)}</button>
              ))}
            </div>
          )}
        </div>

        <label className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#8a93a6]">Note</label>
        <textarea data-testid="compito-note" rows={2} value={form.note} onChange={(e) => set("note", e.target.value)}
          className="w-full mt-1 mb-5 px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C] resize-none" />

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#1A2B4A] rounded-sm hover:bg-[#f9f8f5]">Annulla</button>
          <button data-testid="compito-save" onClick={salva} disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#1A2B4A] text-white rounded-sm hover:bg-[#22345c] disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Assegna
          </button>
        </div>
      </div>
    </div>
  );
}
